import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  authErrorMessage,
  confirmForgotPassword,
  forgotPassword,
  passwordMeetsPolicy,
} from '../auth/cognito';
import {
  AuthLayout,
  FormAlert,
  PasswordChecklist,
  RESET_REQUESTED_NOTICE,
  describedBy,
  readAuthState,
  type AuthRouteState,
} from '../components/AuthLayout';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const arrival = readAuthState(location.state);

  const [email, setEmail] = useState(arrival.email ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [notice, setNotice] = useState(arrival.notice);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const weakPassword = attempted && !passwordMeetsPolicy(password);
  const mismatch = confirm !== '' && confirm !== password;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    setAttempted(true);
    setError(null);

    if (!passwordMeetsPolicy(password)) return;
    if (password !== confirm) return;

    setNotice(undefined);
    setBusy(true);
    const address = email.trim();
    try {
      await confirmForgotPassword(address, code, password);
      const state: AuthRouteState = {
        email: address,
        notice: 'Your password is updated. Sign in with your new one.',
      };
      // Replace: the back button must not return to a form still holding the
      // new password, and the code it used is spent anyway.
      navigate('/login', { state, replace: true });
    } catch (caught: unknown) {
      setError(authErrorMessage(caught, 'resetPassword'));
      setBusy(false);
    }
  }

  async function onResend() {
    if (busy || cooldown > 0) return;

    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
    } catch {
      // Same reasoning as the request page: the answer to "send me a code"
      // cannot vary with whether the address is registered, so the outcome is
      // not inspected and the confirmation below is the only reply.
    }
    setNotice(RESET_REQUESTED_NOTICE);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    setBusy(false);
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Enter the code from your email, then the password you want to use."
      footer={
        <>
          Back to <Link className="link" to="/login">sign in</Link>
        </>
      }
    >
      <form className="stack" onSubmit={onSubmit} aria-busy={busy}>
        <fieldset className="fieldset" disabled={busy}>
          {notice === undefined ? null : <FormAlert kind="success">{notice}</FormAlert>}
          {error === null ? null : (
            <FormAlert kind="error" id="reset-error">
              {error}
            </FormAlert>
          )}

          <div className="field">
            <label className="label" htmlFor="reset-email">
              Email
            </label>
            <input
              id="reset-email"
              className="input"
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy(error !== null && 'reset-error')}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="reset-code">
              Reset code
            </label>
            <input
              id="reset-code"
              className="input mono"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
              }
              minLength={CODE_LENGTH}
              maxLength={CODE_LENGTH}
              required
              autoFocus={email !== ''}
              aria-invalid={error === null ? undefined : true}
              aria-describedby={describedBy(error !== null && 'reset-error')}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="reset-password">
              New password
            </label>
            <input
              id="reset-password"
              className="input"
              type="password"
              name="new-password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={weakPassword ? true : undefined}
              aria-describedby={describedBy(
                'reset-password-rules',
                weakPassword && 'reset-password-error',
              )}
            />
            <PasswordChecklist id="reset-password-rules" value={password} />
            {weakPassword ? (
              <p className="error-text" id="reset-password-error">
                Your password still needs everything ticked above.
              </p>
            ) : null}
          </div>

          <div className="field">
            <label className="label" htmlFor="reset-confirm">
              Confirm new password
            </label>
            <input
              id="reset-confirm"
              className="input"
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              aria-invalid={mismatch ? true : undefined}
              aria-describedby={describedBy(mismatch && 'reset-confirm-error')}
            />
            {mismatch ? (
              <p className="error-text" id="reset-confirm-error">
                Those two passwords are not the same.
              </p>
            ) : null}
          </div>

          <button type="submit" className="btn btn--primary btn--block">
            {busy ? <span className="spinner spinner--sm" aria-hidden="true" /> : null}
            {busy ? 'Saving…' : 'Set new password'}
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => void onResend()}
            disabled={cooldown > 0 || email.trim() === ''}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Send a new code'}
          </button>
        </fieldset>
      </form>
    </AuthLayout>
  );
}
